/** Commercial lines that follow the assessment — moved from the CRM (18 Oct 2026) so the server can build a
 *  Proposal from the measure with the very same rules the CRM screen applies (proposal-from-measure.ts). */
import { selectedSkirtingQuantity } from './proposal-skirting.js';
import { duplicateProposalProduct } from './proposal-product.js';
import { ProposalPricing } from './base-pricing.js';
import { pricingRooms, chargedArea } from './proposal-scope.js';
import { planRunIds, accessoryRunIds } from './proposal-run-ids.js';
const round = (n) => Math.round(n * 100) / 100;
/** Undefined means unresolved, not zero. Never replace a commercial number with invented geometry. */
export function selectionMeasurement(doc, p) {
    const rooms = pricingRooms(doc), selected = p.scope_ids?.length ? rooms.filter(r => p.scope_ids.includes(r.id)) : rooms.filter(r => p.rooms.includes(r.name));
    const expected = p.scope_ids?.length || p.rooms.length;
    if (!expected || selected.length !== expected || selected.some(r => r.area === null))
        return undefined;
    return round(selected.reduce((n, r) => n + (r.area ?? 0), 0));
}
export function serviceMeasurement(line, m) {
    if (line.service_id === 'remove_dispose' || line.service_id === 'remove_only') {
        // A removal line for one existing floor follows that floor's rooms (proposal-removal.ts); a floor no longer present
        // is unresolved rather than zero. Without a floor it is the whole floor area, as before.
        if (line.removal_floor)
            return m.removalGroups?.find(g => g.floor.toLowerCase() === line.removal_floor.toLowerCase())?.area_m2;
        return m.floorAreaM2;
    }
    if (line.service_id === 'skirting')
        return m.skirtingRooms?.length ? selectedSkirtingQuantity(m.skirtingRooms, line.skirting_room_exclusions) : m.skirtingRunM;
    if (line.service_id === 'stair_work' || line.service_id === 'carpet_stair_install')
        return m.stairSteps;
    return undefined;
}
export function accessoryMeasurement(line, m) {
    // A row the site measure built is measured per floor zone when the Proposal is made or refreshed from it
    // (proposal-from-measure.ts); the site-wide figures below would overwrite a zone's area with the whole site's.
    if (line.id.startsWith('measure:'))
        return undefined;
    // A floor-specific accessory cannot inherit the whole site measurement.
    if (line.scope === 'area')
        return ['underlay', 'moisture_barrier', 'adhesive', 'levelling'].includes(line.kind ?? '') ? m.floors?.find(f => f.area_id === line.area_id)?.area_m2 : undefined;
    if (['underlay', 'moisture_barrier', 'adhesive', 'levelling'].includes(line.kind ?? ''))
        return m.floorAreaM2;
    // A trim seeded from the drawing follows its own profile's drawn length; any other trim, the whole drawn run as before.
    if (line.kind === 'trims')
        return line.trim_profile ? m.drawnTrims?.find(t => t.profile.toLowerCase() === line.trim_profile.toLowerCase())?.length_m : m.trimRunM;
    if (line.kind === 'scotia')
        return line.unit === 'LM' ? m.scotiaRunM : undefined;
    if (line.kind === 'smoothedge')
        return m.skirtingRunM;
    return undefined;
}
/** Updates only explicitly following commercial lines. Existing drafts default to manual. */
export function refreshCommercialMeasurements(doc, m) {
    const products = doc.pricing?.products.map(p => {
        const measured = selectionMeasurement(doc, p);
        if (measured === undefined)
            return p;
        const base = { ...p, measured_quantity: measured };
        if (p.quantity_source !== 'assessment')
            return base;
        // Purchased carpet LM cannot be inferred from laid floor m². Keep order quantity and width intact.
        if (p.carpetRollWidthM !== undefined)
            return { ...base, area: measured };
        const quantity = round(measured * (p.quantity_multiplier ?? 1));
        return quantity > 0 ? { ...base, quantity, area: quantity } : base;
    });
    const services = doc.services.map(s => { const measured = serviceMeasurement(s, m); return measured === undefined ? s : { ...s, measured_quantity: measured, ...(s.quantity_source === 'assessment' ? { quantity: measured } : {}) }; });
    // A row that follows the measure also records WHICH runs it follows (the one link, proposal-run-ids.ts); a
    // measure-built row already carries its own.
    const ids = planRunIds(doc);
    const accessories = doc.accessories.map(a => {
        const derived = a.id.startsWith('measure:') ? [] : accessoryRunIds(a, ids);
        const linked = derived.length ? { ...a, run_ids: derived } : a;
        const measured = accessoryMeasurement(a, m);
        return measured === undefined ? linked : { ...linked, measured_quantity: measured, ...(a.quantity_source === 'assessment' ? { quantity: measured } : {}) };
    });
    return { ...doc, ...(doc.pricing && products ? { pricing: ProposalPricing.parse({ ...doc.pricing, products }) } : {}), services, accessories };
}
/** Preserve all non-commercial data; duplication never aliases another line's identity. */
export function duplicateCommercialLine(doc, key, id) {
    const [kind, ...rest] = key.split(':'), sourceId = rest.join(':');
    if (kind === 'product')
        return duplicateProposalProduct(doc, sourceId, id);
    if (kind === 'service') {
        const row = doc.services.find(s => s.id === sourceId);
        if (row)
            return { ...doc, services: [...doc.services, { ...row, id, options: row.options.map(o => ({ ...o })) }] };
    }
    if (kind === 'accessory') {
        const row = doc.accessories.find(a => a.id === sourceId);
        if (row)
            return { ...doc, accessories: [...doc.accessories, { ...row, id }] };
    }
    return doc;
}
export function removeCommercialLine(doc, key) {
    const [kind, ...rest] = key.split(':'), id = rest.join(':');
    if (kind === 'product' && doc.pricing) {
        const removed = doc.pricing.products.find(p => p.id === id), products = doc.pricing.products.filter(p => p.id !== id);
        return { ...doc, pricing: { ...doc.pricing, products }, accessories: doc.accessories.filter(a => !removed || a.bound_to !== removed.productId || products.some(p => p.productId === removed.productId)) };
    }
    if (kind === 'service')
        return { ...doc, services: doc.services.filter(s => s.id !== id) };
    if (kind === 'accessory')
        return { ...doc, accessories: doc.accessories.filter(a => a.id !== id) };
    return doc;
}
/** Returning to assessment replaces the override and persists the actual allowance for future refreshes. */
export function assessmentQuantityPatch(doc, p, parquetForm, rules) {
    const measured = selectionMeasurement(doc, p);
    if (measured === undefined || measured <= 0)
        return null;
    if (p.carpetRollWidthM !== undefined)
        return { quantity_source: 'assessment', measured_quantity: measured, area: measured };
    const multiplier = p.quantity_multiplier ?? (rules ? 1 + chargedArea(measured, parquetForm, rules).waste : undefined);
    if (multiplier === undefined)
        return null;
    const quantity = round(measured * multiplier);
    return { quantity_source: 'assessment', measured_quantity: measured, quantity_multiplier: multiplier, quantity, area: quantity };
}
/**
 * EVERY LINE FOLLOWS THE ASSESSMENT — one click, the same rule each line's own "Follow assessment" applies.
 *
 * Jacky, 15 October 2026, after re-importing Ameer's drawing: *"how do I reset all measurement using
 * assessment"* — every line on that Proposal was a manual override, and "Update measurements" leaves those
 * alone by design. A product takes its measured floor plus its saved allowance (or the rule book's); a
 * service or accessory takes its measured run. A line the drawing cannot measure (trims with no drawn run,
 * a custom service) is left as it is and NAMED, so nothing silently stays manual.
 */
export function followAssessmentEverywhere(doc, m, parquetFormOf, rules) {
    let followed = 0;
    const unresolved = [];
    const products = doc.pricing?.products.map(p => { const patch = assessmentQuantityPatch(doc, p, parquetFormOf(p.productId), rules); if (!patch) {
        unresolved.push(p.description || p.rooms.join(' · ') || p.productId);
        return p;
    } followed += 1; return { ...p, ...patch }; });
    const services = doc.services.map(s => { const measured = serviceMeasurement(s, m); if (measured === undefined) {
        unresolved.push(s.name);
        return s;
    } followed += 1; return { ...s, quantity_source: 'assessment', measured_quantity: measured, quantity: measured }; });
    const accessories = doc.accessories.map(a => { if (!a.on)
        return a; const measured = accessoryMeasurement(a, m); if (measured === undefined) {
        unresolved.push(a.name);
        return a;
    } followed += 1; return { ...a, quantity_source: 'assessment', measured_quantity: measured, quantity: measured }; });
    return { doc: { ...doc, ...(doc.pricing && products ? { pricing: ProposalPricing.parse({ ...doc.pricing, products }) } : {}), services, accessories }, followed, unresolved };
}
//# sourceMappingURL=proposal-commercial.js.map